import React, { useEffect, useRef } from 'react';
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link,
  List as ListIcon,
  ListOrdered,
  Pilcrow,
  Redo2,
  RemoveFormatting,
  Underline,
  Undo2,
} from 'lucide-react';
import {
  legalContentToHtml,
  plainTextToLegalHtml,
  sanitizeLegalHtml,
} from '../utils/legalContent';
import { LEGAL_RICH_CONTENT_CLASSES } from './LegalDocumentContent';

const ToolbarButton = ({ label, children, onAction }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onMouseDown={(event) => {
      event.preventDefault();
      onAction();
    }}
    className="inline-flex h-9 min-w-9 items-center justify-center rounded-lg border border-border bg-card px-2 text-foreground transition-colors hover:bg-secondary"
  >
    {children}
  </button>
);

export function RichLegalTextEditor({
  value,
  onChange,
  expanded = false,
  ariaLabel = 'Formázott jogi dokumentum szövege',
}) {
  const editorRef = useRef(null);
  const emittedValueRef = useRef('');

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || value === emittedValueRef.current) return;
    editor.innerHTML = legalContentToHtml(value);
  }, [value]);

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const sanitized = sanitizeLegalHtml(editor.innerHTML);
    emittedValueRef.current = sanitized;
    onChange(sanitized);
  };

  const runCommand = (command, commandValue = null) => {
    editorRef.current?.focus();
    window.document.execCommand(command, false, commandValue);
    emitChange();
  };

  const insertLink = () => {
    const href = window.prompt('Add meg a hivatkozás címét (https://…):');
    if (!href) return;
    runCommand('createLink', href);
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const clipboard = event.clipboardData;
    const html = clipboard.getData('text/html');
    const pastedContent = html
      ? sanitizeLegalHtml(html)
      : plainTextToLegalHtml(clipboard.getData('text/plain'));
    window.document.execCommand('insertHTML', false, pastedContent);
    emitChange();
  };

  return (
    <div className={`flex min-h-0 flex-col rounded-xl border border-border bg-card ${
      expanded ? 'flex-1' : ''
    }`}>
      <div className="flex flex-wrap gap-1.5 border-b border-border bg-secondary/35 p-2">
        <ToolbarButton label="Normál bekezdés" onAction={() => runCommand('formatBlock', 'p')}>
          <Pilcrow className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Fő alcím" onAction={() => runCommand('formatBlock', 'h2')}>
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Alcím" onAction={() => runCommand('formatBlock', 'h3')}>
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Félkövér" onAction={() => runCommand('bold')}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Dőlt" onAction={() => runCommand('italic')}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Aláhúzott" onAction={() => runCommand('underline')}>
          <Underline className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Felsorolás" onAction={() => runCommand('insertUnorderedList')}>
          <ListIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Számozott lista" onAction={() => runCommand('insertOrderedList')}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Hivatkozás" onAction={insertLink}>
          <Link className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Formázás törlése" onAction={() => runCommand('removeFormat')}>
          <RemoveFormatting className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Visszavonás" onAction={() => runCommand('undo')}>
          <Undo2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Újra" onAction={() => runCommand('redo')}>
          <Redo2 className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder="Másold be ide a Word-dokumentum szövegét, vagy kezdd el a szerkesztést…"
        onInput={emitChange}
        onBlur={(event) => {
          const sanitized = sanitizeLegalHtml(event.currentTarget.innerHTML);
          event.currentTarget.innerHTML = sanitized;
          emittedValueRef.current = sanitized;
          onChange(sanitized);
        }}
        onPaste={handlePaste}
        className={`${LEGAL_RICH_CONTENT_CLASSES} overflow-y-auto px-5 py-4 outline-none empty:before:pointer-events-none empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)] ${
          expanded ? 'min-h-0 flex-1' : 'min-h-[520px] max-h-[70vh]'
        }`}
      />
    </div>
  );
}
